/**
 * 🧑‍🍼 **보호자 함께 읽기 문구를 한 표에서** (2026-10-09).
 *
 * 문구는 마법 장면(`ar.target.<장면>.interact`)의 **누르는 자리 동작**마다 붙는다. 지금까지는
 * [📖 전자책] → 책 → 그 쪽 → 🪄 마법 꾸미기 → 누르는 자리 → 「보호자에게」로 **한 칸씩** 들어가야 했다.
 * 26장면 중 12장면이 남았고(10. 31. 약속) 초안(docs/kpipa/11)도 있다 — 한 표에서 보고 채우게 한다.
 *
 * 🔴 「채웠다」의 셈은 `guardianLines`(ownerTodo) 와 **같아야** 한다 — 표는 다 됐다는데 [✅ 출시 준비]·
 * `pnpm check:owner` 는 남았다고 하면 운영자는 어느 쪽도 안 믿는다. `guardianTable.test.ts` 가 둘을 견준다.
 */
import { AR_INTERACT_LIMITS, type ArInteract } from "./arInteract";

export interface GuardianSpot {
  /** 누르는 자리 id(`h_chick`) */
  id: string;
  /** 운영자가 붙인 이름(「병아리」) */
  label_ko: string;
  /** 아이 말풍선이 있는 자리면 그 말 — 문구를 쓸 때 맥락이 된다 */
  child_ko?: string;
  guardian_ko: string;
}

export interface GuardianScene {
  /** 장면 id(`poljjak.p05` · 경주 카드는 `ar_seokguram`) */
  sceneId: string;
  spots: GuardianSpot[];
  /** 문구가 하나라도 있으면 그 장면은 끝(`guardianLines` 와 같은 셈) */
  done: boolean;
}

const PREFIX = "ar.target.";

/**
 * 아이 화면에 나가는 마법 장면만, 장면 id 순서로. 웹에서 내린 장면(`hidden`·`offWeb`)은 뺀다 —
 * 관리자 매니페스트는 내린 것도 남겨 두지만 아이에게는 안 나간다(`guardianLines` 와 같다).
 */
export function guardianTable(
  assets: Record<string, { interact?: unknown; hidden?: unknown; offWeb?: unknown } | undefined>,
): GuardianScene[] {
  const out: GuardianScene[] = [];
  for (const [key, entry] of Object.entries(assets)) {
    if (!key.startsWith(PREFIX) || !entry?.interact || entry.hidden || entry.offWeb) continue;
    const def = entry.interact as Partial<ArInteract>;
    const spots: GuardianSpot[] = (Array.isArray(def.hotspots) ? def.hotspots : [])
      .filter((h) => h && typeof h.id === "string" && h.action)
      .map((h) => ({
        id: h.id,
        label_ko: typeof h.label_ko === "string" && h.label_ko ? h.label_ko : h.id,
        ...(h.action.kind === "say" && h.action.child_ko ? { child_ko: h.action.child_ko } : {}),
        guardian_ko: typeof h.action.guardian_ko === "string" ? h.action.guardian_ko : "",
      }));
    out.push({ sceneId: key.slice(PREFIX.length), spots, done: spots.some((s) => s.guardian_ko.trim() !== "") });
  }
  return out.sort((a, b) => a.sceneId.localeCompare(b.sceneId, "en", { numeric: true }));
}

/** 문구 한 칸을 다듬는 규칙 — 마법 정의를 읽을 때(`normalizeArInteract`)와 같은 자르기(공백 하나 · 160자). */
export function cleanGuardianLine(text: string): string {
  return text.replace(/\s+/g, " ").trim().slice(0, AR_INTERACT_LIMITS.say);
}

/**
 * 한 자리의 문구를 바꾼 **새** 정의. 빈 글이면 칸을 지운다.
 * 🔴 그 자리가 없으면 `null` — 그 사이 누가 자리를 지웠다. 조용히 넘기면 「올렸어요」인데 안 들어간다.
 */
export function withGuardianLine(def: ArInteract, hotspotId: string, text: string): ArInteract | null {
  if (!def.hotspots.some((h) => h.id === hotspotId)) return null;
  const line = cleanGuardianLine(text);
  return {
    ...def,
    hotspots: def.hotspots.map((h) => {
      if (h.id !== hotspotId) return h;
      const { guardian_ko: _old, ...rest } = h.action;
      void _old;
      return { ...h, action: (line ? { ...rest, guardian_ko: line } : rest) as typeof h.action };
    }),
  };
}

export interface GuardianDraft {
  scene: string;
  hotspot: string;
  guardian_ko: string;
}

/** 초안 파일(`content/guardian-drafts.json`) — 바깥 파일이라 모양을 확인하고 받는다(불변식 7). */
export function normalizeGuardianDrafts(raw: unknown): GuardianDraft[] {
  const list = (raw as { drafts?: unknown } | null)?.drafts;
  if (!Array.isArray(list)) return [];
  const out: GuardianDraft[] = [];
  for (const d of list) {
    const o = d as Record<string, unknown> | null;
    if (!o || typeof o.scene !== "string" || typeof o.hotspot !== "string" || typeof o.guardian_ko !== "string") continue;
    const text = cleanGuardianLine(o.guardian_ko);
    if (text) out.push({ scene: o.scene, hotspot: o.hotspot, guardian_ko: text });
  }
  return out;
}

/** 표의 칸 이름 — 장면과 자리를 한 줄로. */
export const guardianCell = (sceneId: string, hotspotId: string) => `${sceneId}#${hotspotId}`;

/**
 * 초안을 **빈 칸에만** 채운다 — 게시본에 문구가 있거나 운영자가 이미 쓰고 있는 칸은 안 건드린다
 * (묻지 않고 덮지 않는다). 표에 없는 장면·자리의 초안은 버린다(그 사이 자리가 바뀌었다).
 * @returns 채운 칸들(칸 이름 → 글)
 */
export function fillGuardianDrafts(
  table: readonly GuardianScene[],
  edits: Readonly<Record<string, string>>,
  drafts: readonly GuardianDraft[],
): Record<string, string> {
  const filled: Record<string, string> = {};
  for (const d of drafts) {
    const spot = table.find((s) => s.sceneId === d.scene)?.spots.find((h) => h.id === d.hotspot);
    if (!spot) continue;
    const cell = guardianCell(d.scene, d.hotspot);
    const now = edits[cell] ?? spot.guardian_ko;
    if (now.trim() !== "") continue;
    filled[cell] = d.guardian_ko;
  }
  return filled;
}
